import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { Category } from "@common/types/category";
import { updateCategoryBodySchema } from "@common/schemas/category.schema";
import {
    useUpdateCategoryGlMappingsMutation,
    useUpdateCustomCategoryMutation,
} from "@api/category.api";
import { useIsCategoryGlMappingEnabled } from "@hooks/useIsCategoryGlMappingEnabled";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ApplyRuleToExistingTransactions } from "@components/settings/Rules/TransactionRuleForm/ApplyRuleToExistingTransactions";
import { HStack } from "@design-system/HStack/HStack";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { VStack } from "@design-system/VStack/VStack";
import { createRulesUrl } from "@components/settings/Rules/lib/rulesUrl";
import { RuleGroupType } from "@common/types/transactionRule";
import {
    CategoryGlMappingGrid,
    UseCategoryGlMappingGridResult,
    useCategoryGlMappingGrid,
    useReconstructedOverrideRows,
} from "./CategoryGlMappingGrid";
import { isCustomBusinessSubcategory } from "./categoryGlGridEligibility";
import styles from "./CreateCategoryModal.module.scss";

interface UpdateCategoryFormValues extends z.infer<
    typeof updateCategoryBodySchema
> {
    applyToExistingTransactions: boolean;
}

const fields = createFormikFields<UpdateCategoryFormValues>();

const AdvancedRuleNotice = ({ categoryId }: { categoryId: string }) => (
    <p className="mb-0">
        This subcategory posts through an advanced accounting rule.{" "}
        <StateAwareLink
            to={createRulesUrl({
                tab: RuleGroupType.ACCOUNTING,
                filters: { categoryId },
            })}
        >
            Edit it on the rules page ↗
        </StateAwareLink>
    </p>
);

const GlMappingSection = ({
    isLoading,
    isError,
    isAdvanced,
    categoryId,
    grid,
}: {
    isLoading: boolean;
    isError: boolean;
    isAdvanced: boolean;
    categoryId: string;
    grid: UseCategoryGlMappingGridResult;
}) => {
    if (isLoading) {
        return <p className="mb-0">Loading accounting mapping...</p>;
    }
    if (isError) {
        return (
            <p className="mb-0">
                Couldn't load the accounting mapping. Close and reopen to try
                again.
            </p>
        );
    }
    if (isAdvanced) {
        return <AdvancedRuleNotice categoryId={categoryId} />;
    }
    return (
        <>
            <CategoryGlMappingGrid grid={grid} />
            <ApplyRuleToExistingTransactions label="Do not apply this mapping to existing transactions" />
        </>
    );
};

export const UpdateCategoryModal = ({
    category,
    onHide,
}: {
    category: Category;
    onHide: () => void;
}) => {
    const { error: errorToast } = useToaster();
    const updateMutation = useUpdateCustomCategoryMutation(category);
    const updateGlMappingsMutation = useUpdateCategoryGlMappingsMutation();
    const isGridEnabled =
        useIsCategoryGlMappingEnabled() &&
        isCustomBusinessSubcategory(category);

    const { initialRows, isAdvanced, isLoading, isError } =
        useReconstructedOverrideRows({
            categoryId: category.id,
            isEnabled: isGridEnabled,
        });

    const grid = useCategoryGlMappingGrid({
        parentCategoryId: category.parentCategoryId ?? "",
        isEnabled: isGridEnabled,
        initialRows,
    });

    const isSaving =
        updateMutation.isPending || updateGlMappingsMutation.isPending;
    const isGlMappingLoading = isGridEnabled && (isLoading || grid.isLoading);
    const isGlMappingError = isGridEnabled && isError;
    const shouldSaveGlMappings =
        isGridEnabled && !isAdvanced && initialRows !== undefined;

    const formik = useFormik<UpdateCategoryFormValues>({
        initialValues: {
            label: category.label ?? "",
            description: category.description ?? "",
            applyToExistingTransactions: true,
        },
        validate: validateFormikWithZodSchema(updateCategoryBodySchema),
        onSubmit: submitHelper({
            loading: isSaving,
            handler: async (data) => {
                await updateMutation.mutateAsync({
                    description: data.description ?? null,
                    label: data.label,
                });
                if (shouldSaveGlMappings) {
                    await updateGlMappingsMutation.mutateAsync({
                        categoryId: category.id,
                        overrides: grid.toOverridesPayload(),
                        applyToExistingTransactions:
                            data.applyToExistingTransactions,
                    });
                }
                onHide();
            },
            failureHandler: async () => {
                errorToast("Failed to save subcategory");
            },
        }),
        validateOnMount: true,
    });

    return (
        <>
            <StandardModalHeader>Update Subcategory</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16} className={styles.fields}>
                        <fields.Text name="label" label="Label" size="xl" />
                        <fields.Text
                            name="description"
                            label="Description"
                            size="xl"
                        />
                    </VStack>

                    {isGridEnabled && (
                        <GlMappingSection
                            isLoading={isLoading}
                            isError={isError}
                            isAdvanced={isAdvanced}
                            categoryId={category.id}
                            grid={grid}
                        />
                    )}

                    <HStack as="footer" justify="end" gap={12}>
                        <Button
                            variant="secondary"
                            size="xl"
                            type="button"
                            onClick={onHide}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>

                        <ButtonWithLoader
                            variant="primary"
                            size="xl"
                            loading={isSaving}
                            type="submit"
                            disabled={
                                !formik.isValid ||
                                isGlMappingLoading ||
                                isGlMappingError
                            }
                            hotKey="mod+enter"
                            hotKeyOptions={{
                                description: "Save subcategory",
                                preventDefault: true,
                                enableOnFormTags: true,
                            }}
                        >
                            Save changes
                        </ButtonWithLoader>
                    </HStack>
                </Form>
            </FormikProvider>
        </>
    );
};
